import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Briefcase, Compass, LayoutGrid, Plus, Download, X, RefreshCw, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import { onInstallAvailable, promptInstall, isStandalone as pwaStandalone, updateApp } from "../lib/pwaInstall";

// Guest / marketing tab bar — matches owner mockup: Home / Trips / [Plan +] / Explore / More
const TABS = [
  { to: "/", label: "Home", icon: Home, match: (p) => p === "/" },
  { to: "/travel-hub", label: "Trips", icon: Briefcase, match: (p) => p.startsWith("/travel-hub") || p.startsWith("/trips") },
  { fab: true, to: "/plan-ai", label: "Plan", icon: Plus, match: () => false },
  { to: "/explore-places", label: "Explore", icon: Compass, match: (p) => p.startsWith("/explore") || p.startsWith("/destinations") },
  { to: "/features", label: "More", icon: LayoutGrid, match: (p) => p.startsWith("/features") || p.startsWith("/dashboard") || p.startsWith("/login") },
];

// Signed-in: Home / Trips / [Plan +] / Vaults / More — Phase 1 primary tabs
const APP_TABS = [
  { to: "/", label: "Home", icon: Home, match: (p) => p === "/" },
  { to: "/travel-hub", label: "Trips", icon: Briefcase,
    match: (p) => p.startsWith("/travel-hub") || p.startsWith("/trips") },
  { fab: true, to: "/plan-ai", label: "Plan", icon: Plus, match: () => false },
  { to: "/vault", label: "Vaults", icon: ShieldCheck,
    match: (p) => p.startsWith("/vault") || p.startsWith("/locker") || p.startsWith("/wallet") },
  { to: "/my-social-pulse", label: "More", icon: LayoutGrid,
    match: (p) => p.startsWith("/my-social-pulse") || p.startsWith("/features") || p.startsWith("/dashboard") },
];

const isStandalone = () => pwaStandalone();

/* Native-app feel: thumb-friendly bottom tab bar (mobile only) + install banner. */
export function MobileBottomNav() {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const nav = useNavigate();
  if (pathname.startsWith("/auth/") || pathname.startsWith("/app-login")) return null;
  const q = new URLSearchParams(search);
  const tabs = user ? APP_TABS : TABS;
  return (
    <nav data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur-xl border-t border-border pb-[env(safe-area-inset-bottom)] shadow-warm-lg">
      <div className="grid grid-cols-5 relative">
        {tabs.map(({ to, label, icon: Icon, match, fab }, i) => {
          const active = match(pathname, q);
          if (fab) return (
            <div key="fab" className="relative flex justify-center">
              <button data-testid="bottomnav-plan" aria-label="Plan"
                onClick={() => nav(to)}
                className="absolute -top-6 w-14 h-14 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shadow-warm-lg ring-4 ring-background hover:scale-105 active:scale-95 transition-transform">
                <Icon className="w-6 h-6" strokeWidth={2.5} />
              </button>
              <span className="pt-8 text-[10px] font-700 text-muted-foreground">{label}</span>
            </div>
          );
          return (
            <Link key={label} to={to} data-testid={`bottomnav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex flex-col items-center gap-0.5 py-2 text-[10px] font-700 transition-colors ${active ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
              <Icon className={`w-5 h-5 ${active ? "stroke-[2.5]" : ""}`} strokeWidth={active ? 2.5 : 2} />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export function InstallPrompt() {
  const [canPrompt, setCanPrompt] = useState(false);
  const [guide, setGuide] = useState(false);
  // Dismissal now EXPIRES after 7 days (was forever) so the banner returns for repeat visitors
  const [dismissed, setDismissed] = useState(() => {
    try {
      const at = Number(localStorage.getItem("tp_pwa_dismissed_at") || 0);
      return at > 0 && Date.now() - at < 7 * 864e5;
    } catch { return false; }
  });
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isCriOS = /crios/i.test(navigator.userAgent); // Chrome on iOS
  const { pathname } = useLocation();

  useEffect(() => {
    const unsub = onInstallAvailable(setCanPrompt);
    const openGuide = () => setGuide(true); // "Download our Web App" links site-wide
    window.addEventListener("tp:install-guide", openGuide);
    return () => {
      unsub();
      window.removeEventListener("tp:install-guide", openGuide);
    };
  }, []);

  const dismiss = () => { setDismissed(true); localStorage.setItem("tp_pwa_dismissed_at", String(Date.now())); };
  const install = async () => {
    if (!canPrompt) { setGuide(true); return; }
    const outcome = await promptInstall();
    // Only hide the banner if they actually installed — cancelling keeps it visible
    if (outcome === "accepted") { setCanPrompt(false); dismiss(); }
  };

  if (isStandalone() || pathname === "/get-app" || pathname === "/social-pulse-app") return null;

  const isMobile = /iphone|ipad|ipod|android/i.test(navigator.userAgent);
  const appLink = window.location.origin; // direct site link — no shortener
  const waText = encodeURIComponent(
    `Install the My Social Pulse app \u{1F4F1} Open this link on your phone, then tap Share \u2192 "Add to Home Screen":\n${appLink}`
  );
  const waHref = `https://wa.me/?text=${waText}`;

  const steps = !isMobile
    ? [
        ["Point your phone camera at the QR code below — it opens My Social Pulse on your phone.", "1"],
        ["Or tap the green WhatsApp button to send yourself the link instead.", "2"],
        ["On your phone: iPhone \u2192 Share \u2192 \u201cAdd to Home Screen\u201d; Android \u2192 tap Install when prompted.", "3"],
      ]
    : isIOS
    ? [
        [isCriOS ? "Tap the Share icon at the top right of Chrome (a square with an arrow pointing up)."
                 : "Tap the Share icon at the bottom of Safari (a square with an arrow pointing up).", "1"],
        ["Scroll down the menu and tap \u201cAdd to Home Screen\u201d.", "2"],
        ["Tap \u201cAdd\u201d — the My Social Pulse icon appears on your home screen and opens full-screen like a real app.", "3"],
      ]
    : [
        ["Open your browser menu (\u22ee at the top right).", "1"],
        ["Tap \u201cInstall app\u201d or \u201cAdd to Home screen\u201d.", "2"],
        ["Confirm — the My Social Pulse icon appears on your home screen.", "3"],
      ];

  return (
    <>
      {guide && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center" role="dialog" aria-modal="true" aria-label="Install the My Social Pulse web app" data-testid="pwa-install-guide">
          <div className="absolute inset-0 bg-black/50" onClick={() => setGuide(false)} />
          <div className="relative w-full md:max-w-md bg-white rounded-t-2xl md:rounded-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <img src="/pwa-icon-192.png" alt="My Social Pulse" className="w-12 h-12 rounded-xl shrink-0" />
              <div className="min-w-0">
                <div className="font-heading font-900 text-lg leading-tight">Install My Social Pulse</div>
                <div className="text-xs text-muted-foreground">
                  {!isMobile ? "You're on a computer — send the link to your phone and install it there:"
                    : isIOS ? "iPhones don't show an Install button — Apple requires these 3 taps instead:"
                    : "3 quick taps and it lives on your home screen:"}
                </div>
              </div>
              <button onClick={() => setGuide(false)} data-testid="pwa-guide-close" className="ml-auto shrink-0 text-zinc-400 hover:text-zinc-800"><X className="w-5 h-5" /></button>
            </div>
            {/* PROMINENT: 1-click install (when the browser allows it) + 1-click update */}
            {canPrompt && (
              <button onClick={async () => { const o = await promptInstall(); if (o === "accepted") { setCanPrompt(false); setGuide(false); dismiss(); } }}
                data-testid="pwa-guide-install-now"
                className="mb-4 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white px-4 py-3 text-sm font-800 rounded-full hover:bg-emerald-700 transition-colors">
                <Download className="w-4 h-4" /> Install now — one tap, no steps needed
              </button>
            )}
            <div className="mb-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5 text-xs text-blue-900" data-testid="pwa-guide-autoupdate-note">
              <RefreshCw className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>Already installed? The app <b>updates itself automatically</b> — or <button onClick={updateApp} className="underline font-700" data-testid="pwa-guide-update-now">update to the latest version now</button> with one click.</span>
            </div>
            <ol className="space-y-3">
              {steps.map(([text, n]) => (
                <li key={n} className="flex items-start gap-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-white font-heading font-900 text-sm flex items-center justify-center">{n}</span>
                  <span className="text-sm text-zinc-700 pt-1">{text}</span>
                </li>
              ))}
            </ol>
            {/* Always show the typeable short URL — the last-resort path on any device */}
            <div className="mt-4 border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700" data-testid="pwa-guide-typed-link">
              Nothing working? Type this in any browser: <span className="font-mono font-bold text-primary break-all">{appLink.replace("https://", "")}</span>
            </div>
            {!isMobile && (
              <div className="mt-5 flex items-center gap-4 border border-zinc-200 bg-zinc-50 p-4 rounded-xl" data-testid="pwa-install-qr">
                <div className="shrink-0 bg-white p-2 border border-zinc-200 rounded-lg">
                  <QRCodeSVG value={appLink} size={104} level="M" includeMargin={false} />
                </div>
                <div className="min-w-0 text-sm text-zinc-700">
                  <div className="font-700 mb-0.5">Fastest way: scan with your phone camera</div>
                  <div className="text-xs text-muted-foreground">It opens the site on your phone — then tap <b>Install</b> (Android) or follow the 3-tap guide (iPhone).</div>
                </div>
              </div>
            )}
            <a href={waHref} target="_blank" rel="noreferrer" data-testid="pwa-whatsapp-send"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-2.5 text-sm font-700 rounded-full hover:opacity-90 transition-opacity">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              {isMobile ? "Share the app link on WhatsApp" : "Send the link to my phone (WhatsApp)"}
            </a>
            <div className="mt-5 border-t border-zinc-100 pt-3 text-xs text-muted-foreground">
              Once installed it opens full-screen with its own icon — no browser bars — and your trips keep working offline.
            </div>
          </div>
        </div>
      )}
      {!dismissed && (
        <div data-testid="pwa-install-banner" onClick={install} role="button" tabIndex={0}
          className="fixed bottom-16 md:bottom-4 left-3 right-3 md:left-auto md:right-4 md:max-w-sm z-50 bg-blue-700 text-white p-3.5 shadow-2xl flex items-center gap-3 rounded-xl cursor-pointer hover:bg-blue-800 transition-colors">
          <img src="/pwa-icon-192.png" alt="My Social Pulse" className="w-10 h-10 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-700">Get the My Social Pulse app</div>
            <div className="text-xs text-blue-100">{isMobile ? "Tap here to install — works offline on trips." : "Scan a QR and it's on your phone in seconds."}</div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); install(); }} data-testid="pwa-install-btn"
            className="shrink-0 inline-flex items-center gap-1.5 bg-orange-500 text-white px-3 py-1.5 text-xs font-700 rounded-full hover:bg-orange-600">
            <Download className="w-3.5 h-3.5" /> {canPrompt ? "Install" : "Show me how"}
          </button>
          <button onClick={(e) => { e.stopPropagation(); dismiss(); }} data-testid="pwa-install-dismiss" className="shrink-0 text-blue-200 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </>
  );
}
